import Hls from 'hls.js';

import { Parser as m3u8Parser } from 'm3u8-parser';

type ProgressCallback = (percentage: number) => void;

export function onLoadHlsProgress(hls: Hls, callback: ProgressCallback): void {
  const progressPollingMs = 250;
  let currentFragStats: any;
  let alreadyLoaded = 0;
  let isFirstLoad = true;

  hls.on(Hls.Events.FRAG_LOADING, (ev: any, data: any) => {
    currentFragStats = data.frag.stats;
    alreadyLoaded = 0;
    if (isFirstLoad) {
      isFirstLoad = false;
      reportProgress();
    }
  });

  hls.on(Hls.Events.ERROR, (ev: any, data: any) => {
    if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR && currentFragStats) {
      alreadyLoaded = currentFragStats.loaded;
      reportProgress();
    }
  });

  function reportProgress() {
    function pollProgress() {
      if (currentFragStats) {
        const { loaded, total } = currentFragStats;
        if (total - alreadyLoaded > 0) {
          const percentage = Math.round(((loaded - alreadyLoaded) / (total - alreadyLoaded)) * 100);
          callback(percentage);
        } else {
          callback(0);
        }
      }
    }

    pollProgress();
    const intervalId = setInterval(pollProgress, progressPollingMs);

    hls.once(Hls.Events.FRAG_LOADED, () => {
      callback(100);
      clearInterval(intervalId);
    });
  }
}

// Parse the m3u8 file to get the total duration
export const fetchM3U8Duration = async (url: string): Promise<{ totalDuration: number; isEnded: boolean }> => {
  try {
    const response = await fetch(url);
    const data = await response.text();
    const parser = new m3u8Parser();
    parser.push(data);
    parser.end();

    // console.log('Manifest:', parser.manifest);

    // If this is a master playlist (contains multiple sub-playlists)
    if (parser.manifest.playlists && parser.manifest.playlists.length > 0) {
      const mediaPlaylistUrl = parser.manifest.playlists[0].uri;

      // Construct the complete sub-playlist URL using `new URL`
      const absoluteMediaPlaylistUrl = new URL(mediaPlaylistUrl, url).toString();
      // console.log('Redirecting to media playlist:', absoluteMediaPlaylistUrl);

      // Recursively parse the sub-playlist
      return fetchM3U8Duration(absoluteMediaPlaylistUrl);
    }

    // Handle media playlist
    const segments: any = parser.manifest.segments || [];
    // console.log('Segments:', segments);

    const totalDuration = segments.reduce((sum: any, segment: any) => sum + segment.duration, 0);

    // Check for the `EXT-X-ENDLIST` indicator
    const isEnded = !!parser.manifest.endList;

    return { totalDuration, isEnded };
  } catch (error) {
    console.error('Error fetching or parsing m3u8 file:', error);
    return { totalDuration: 0, isEnded: true }; // Assume ended if an error occurs
  }
};

const ua = window.navigator.userAgent;
const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i); // Check if it's an iOS device
const webkit = !!ua.match(/WebKit/i); // Check if it uses the WebKit engine
const isSafari = /Safari/i.test(ua) && !/CriOS/i.test(ua); // Check if it's Safari (not Chrome)
const isChrome = /CriOS/i.test(ua); // Check if it's Chrome on iOS

// More precise condition to check if it's iOS Safari
export const iOSSafari = iOS && webkit && isSafari && !isChrome && navigator.vendor === 'Apple Computer, Inc.';
